import {
  askApiV1MediaChatConversationsConversationIdAskPost,
  deleteConversationApiV1MediaChatConversationsConversationIdDelete,
  getAiCreditsApiV1MyAccountAiCreditsGet,
  getHistoryApiV1MediaChatConversationsConversationIdHistoryGet,
  getJobStatusApiV1MediaChatJobsJobIdGet,
  listConversationsApiV1MediaChatConversationsGet,
  renameConversationApiV1MediaChatConversationsConversationIdPatch,
  startConversationApiV1MediaChatConversationsPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ApiMediaChatJobStatusResponse as JobStatusResponse,
  AskResponse,
} from "@/backend-sdk/client/types.gen";
import {
  AiCreditsExhaustedError,
  STATUS_SEM_CREDITOS,
} from "@/services/ai-credit-packs.service";

/**
 * Serviço do chat assíncrono de "Performance na Mídia".
 *
 * Usa o SDK tipado gerado a partir do OpenAPI do backend. O backend faz proxy
 * para o Nexus; o fluxo é: resolver conversa -> perguntar (retorna job_id na
 * hora) -> fazer polling do status até COMPLETED/FAILED.
 */

const IGNORE_FILTERS_HEADERS = { "x-ignore-global-filters": "1" } as const;

/** customer_uuid da conta/subconta selecionada (mesmo valor que os dashboards). */
function activeCustomerUuid(): string | undefined {
  return localStorage.getItem("active_customer_uuid") ?? undefined;
}

export type MediaChatJobStatus =
  | "QUEUED"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED";

export interface MediaChatPhase {
  key: string;
  label: string;
  at: string;
  /** Plataformas consultadas nesta etapa (ga4, google_ads, meta_ads,
   * search_console, instagram). Ausente em fases gravadas antes do campo. */
  sources?: string[];
}

export interface MediaChatJob {
  jobId: string;
  conversationId: string;
  status: MediaChatJobStatus;
  phases: MediaChatPhase[];
  answer?: string | null;
  error?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  // Saldo de créditos de IA após o débito desta pergunta (null = ilimitado).
  creditsRemaining?: number | null;
}

export interface AICredits {
  unlimited: boolean;
  balance: number | null;
}

export interface MediaChatHistoryMessage {
  role: "user" | "assistant";
  content: string;
  created_at?: string | null;
}

export interface ResolvedConversation {
  conversationId: string;
  messages: MediaChatHistoryMessage[];
}

export interface ConversationSummary {
  conversationId: string;
  title: string | null;
  updatedAt: string | null;
}

function mapJob(data: JobStatusResponse | AskResponse): MediaChatJob {
  const full = data as JobStatusResponse;
  const ask = data as AskResponse;
  return {
    jobId: String(data.job_id),
    conversationId: String(data.conversation_id),
    status: data.status as MediaChatJobStatus,
    phases: (full.phases ?? []) as unknown as MediaChatPhase[],
    answer: full.answer ?? null,
    error: full.error ?? null,
    createdAt: full.created_at ?? null,
    updatedAt: full.updated_at ?? null,
    startedAt: full.started_at ?? null,
    finishedAt: full.finished_at ?? null,
    creditsRemaining: ask.credits_remaining ?? null,
  };
}

class MediaChatService {
  /** Lista as conversas da conta selecionada (multi-chat). */
  async listConversations(
    accountId?: string,
  ): Promise<{ conversations: ConversationSummary[]; limit: number }> {
    const customerUuid = activeCustomerUuid();
    const query: { account_id?: string; customer_uuid?: string } = {};
    if (accountId) query.account_id = accountId;
    if (customerUuid) query.customer_uuid = customerUuid;
    const { data, error } = await listConversationsApiV1MediaChatConversationsGet({
      headers: IGNORE_FILTERS_HEADERS,
      query,
    });
    if (error || !data) {
      throw new Error("Não foi possível carregar as conversas");
    }
    const raw = data as {
      conversations?: Array<{
        conversation_id: string;
        title?: string | null;
        updated_at?: string | null;
      }>;
      limit?: number;
    };
    return {
      conversations: (raw.conversations ?? []).map((c) => ({
        conversationId: String(c.conversation_id),
        title: c.title ?? null,
        updatedAt: c.updated_at ?? null,
      })),
      limit: raw.limit ?? 20,
    };
  }

  /** Cria uma conversa nova (botão "Nova conversa"). */
  async createConversation(accountId?: string): Promise<string> {
    const { data, error } = await startConversationApiV1MediaChatConversationsPost({
      headers: IGNORE_FILTERS_HEADERS,
      body: { account_id: accountId, customer_uuid: activeCustomerUuid() },
    });
    if (error || !data) {
      const detail = (error as { detail?: unknown } | undefined)?.detail;
      throw new Error(
        typeof detail === "string" ? detail : "Não foi possível criar a conversa",
      );
    }
    return String((data as { conversation_id: string }).conversation_id);
  }

  async renameConversation(conversationId: string, title: string): Promise<void> {
    const { error } = await renameConversationApiV1MediaChatConversationsConversationIdPatch(
      {
        headers: IGNORE_FILTERS_HEADERS,
        path: { conversation_id: conversationId },
        body: { title },
      },
    );
    if (error) throw new Error("Não foi possível renomear a conversa");
  }

  async deleteConversation(conversationId: string): Promise<void> {
    const { error } = await deleteConversationApiV1MediaChatConversationsConversationIdDelete(
      {
        headers: IGNORE_FILTERS_HEADERS,
        path: { conversation_id: conversationId },
      },
    );
    if (error) throw new Error("Não foi possível apagar a conversa");
  }

  /** Histórico de uma conversa específica (ao trocar de conversa). */
  async getHistory(conversationId: string): Promise<MediaChatHistoryMessage[]> {
    const { data, error } =
      await getHistoryApiV1MediaChatConversationsConversationIdHistoryGet({
        headers: IGNORE_FILTERS_HEADERS,
        path: { conversation_id: conversationId },
        query: { limit: 100 },
      });
    if (error || !data) return [];
    const raw = data as { messages?: MediaChatHistoryMessage[] };
    return raw.messages ?? [];
  }

  async ask(conversationId: string, message: string): Promise<MediaChatJob> {
    // Sem IGNORE_FILTERS_HEADERS de propósito: o /ask é o único endpoint do
    // chat que PRECISA dos filtros globais — o start_date/end_date anexado pelo
    // cliente HTTP é o período que a tela está exibindo, e é ele que o backend
    // repassa ao Nexus para o agente responder o mesmo intervalo do dashboard.
    const { data, error, response } = await askApiV1MediaChatConversationsConversationIdAskPost({
      path: { conversation_id: conversationId },
      body: { message, customer_uuid: activeCustomerUuid() },
    });
    if (error || !data) {
      // Repassa o detalhe do backend (ex.: "Créditos de IA insuficientes...").
      const detail = (error as { detail?: unknown } | undefined)?.detail;
      const message =
        typeof detail === "string" ? detail : "Não foi possível enviar a mensagem";
      // <M-280> Sem crédito não é falha: a tela oferece a compra.
      if (response?.status === STATUS_SEM_CREDITOS) {
        throw new AiCreditsExhaustedError(message);
      }
      throw new Error(message);
    }
    return mapJob(data);
  }

  /** Saldo de créditos de IA da loja ativa: franquia do mês mais o comprado em pacote. */
  async getCredits(): Promise<AICredits> {
    const customerUuid = activeCustomerUuid();
    const { data, error } = await getAiCreditsApiV1MyAccountAiCreditsGet({
      headers: IGNORE_FILTERS_HEADERS,
      query: customerUuid ? { customer_uuid: customerUuid } : {},
    });
    if (error || !data) {
      // Não mascarar erro como "0 créditos": deixa o caller manter o valor atual.
      throw new Error("Não foi possível carregar o saldo de créditos");
    }
    // <M-280> O saldo que se gasta é o total — a franquia do mês mais o comprado em pacote —,
    // o mesmo número que `credits_remaining` devolve depois de cada pergunta.
    return {
      unlimited: data.unlimited,
      balance: data.total_balance ?? data.balance ?? null,
    };
  }

  async getJobStatus(jobId: string): Promise<MediaChatJob> {
    const { data, error } = await getJobStatusApiV1MediaChatJobsJobIdGet({
      headers: IGNORE_FILTERS_HEADERS,
      path: { job_id: jobId },
    });
    if (error || !data) {
      throw new Error("Não foi possível consultar o status");
    }
    return mapJob(data);
  }
}

export const mediaChatService = new MediaChatService();
